import * as React from "react";
import { mergeOverrides } from "baseui";
import { Check } from "baseui/icon";
import { Input as BWInput, InputProps, SIZE } from "baseui/input";

import Visible from "../icons/Visible";
import Hidden from "../icons/Hidden";
import { expandBorder, expandBorderRadius, expandPadding } from "../utils/styles";

type TInputProps = InputProps & Record<string, TODO>;

export const Input: React.FC<TInputProps> = (props) => {
  const { overrides = {}, disabled, ...restProps } = props;
  return (
    <BWInput
      overrides={mergeOverrides(getInputOverrides(props), overrides)}
      endEnhancer={!disabled && props.positive ? PositiveEndEnhancer : undefined}
      disabled={disabled}
      {...restProps}
    />
  );
};

export const PositiveEndEnhancer: React.FC<{ $isFocused?: boolean }> = ({
  $isFocused,
}) => {
  return !$isFocused && <Check size={24} color="inputBorderPositive" />;
};

export function getInputOverrides({
  color = null,
  type = null,
  size = SIZE.default,
}: TInputProps): InputProps["overrides"] {
  return {
    Root: {
      style: ({ $disabled, $theme }) => ({
        paddingRight: 0, // Removes space after the endEnhancer
        ...expandBorder(["none", "none", "solid", "none"], "Style"),
        // Baseweb v12 default border radius for Input is radius300. Override it to 0 to
        // match designs
        ...expandBorderRadius(0),
        borderBottomWidth: size === SIZE.mini ? 0 : "1px",
        backgroundColor: "transparent",
        ...($disabled && { borderBottomColor: $theme.colors.mono400 }),
      }),
    },
    InputContainer: {
      style: {
        outline: "none",
        backgroundColor: "transparent",
      },
    },
    Input: {
      style: ({ $theme }) => ({
        ...getInputColor($theme, color),
        ...expandPadding([0, 0, "10px", 0]),
        ...getBrowserOverrides(type),
        ...getSizeOverrides(size, $theme),
        minWidth: "20px",
      }),
    },
    EndEnhancer: {
      style: {
        backgroundColor: "transparent",
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    MaskToggleHideIcon: () => <Hidden size={20} />,
    MaskToggleShowIcon: () => <Visible size={20} />,
  };
}

function getBrowserOverrides(type) {
  if (type === "number") {
    /*
      Hide spinner arrows on Chrome, Safari, Firefox, Edge
      See https://stackoverflow.com/questions/3790935/can-i-hide-the-html5-number-input-s-spin-box
    */
    return {
      "::-webkit-outer-spin-button,::-webkit-inner-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
      "-moz-appearance": "textfield",
    };
  }
  return {};
}

function getSizeOverrides(size, $theme) {
  if (size === SIZE.mini) {
    return {
      paddingBottom: "0px",
      fontSize: $theme.typography.font550.fontSize,
    };
  }
  return {};
}

function getInputColor(theme, color) {
  const style = {};
  if (color) {
    style.color = theme.colors[color] || color;
  }
  return style;
}

Input.SIZE = SIZE;
